/**
 * Pure (no JSX) helpers for chart datapoint selection — click, highlight, and
 * dim logic shared across all cartesian chart cases.
 */

import type { KeyboardEvent } from 'react';
import { useCallback, useRef } from 'react';

import type { ChartDatapointSelection } from '@/types/reports';

export type { ChartDatapointSelection } from '@/types/reports';

/**
 * Read the `__sourceRowIndex` stamp from a Recharts datum object.
 * Returns null when the field is absent or not a finite integer (e.g. the
 * synthesised "Other" row produced by applyTopN has no stamp).
 */
export function sourceRowIndexOf(datum: unknown): number | null {
  if (datum == null || typeof datum !== 'object') {
    return null;
  }
  const raw = (datum as Record<string, unknown>)['__sourceRowIndex'];
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return null;
  }
  return raw;
}

/**
 * Recharts hands a click/hover its datum in one of three shapes, depending on
 * which series fired it:
 *
 *   - Bar / Cell  → the datum itself
 *   - Line / Area → a chart state, `{ activePayload: [{ payload: datum }] }`
 *   - Pie         → the sector's props, which both spread the datum's own
 *                   fields onto the object and carry it again under `payload`
 *
 * Try each in turn and take the first that yields a stamped row index, so one
 * handler factory serves every series type.
 */
export function resolveSourceRowIndex(data: unknown): number | null {
  const obj = data as Record<string, unknown> | null | undefined;
  const candidates: unknown[] = [
    (obj?.activePayload as Array<{ payload?: unknown }> | undefined)?.[0]
      ?.payload,
    data,
    obj?.payload,
  ];
  for (const candidate of candidates) {
    const rowIndex = sourceRowIndexOf(candidate);
    if (rowIndex !== null) {
      return rowIndex;
    }
  }
  return null;
}

/**
 * A content fingerprint of the chart's rows: an ordered list of each row's
 * `__sourceRowIndex` stamp (placeholder for unstamped rows) PAIRED WITH its
 * identity label, JSON-encoded so no label value can forge a delimiter and make
 * two distinct row sequences collide. Callers build `chartData` inline in their
 * render body, so it is a fresh array on every render even when nothing changed
 * — reference equality can't tell a benign re-render from a real data change.
 *
 * The identity label is essential, not decorative: `__sourceRowIndex` is just
 * the row's position (0,1,2,…) regenerated from each response, so the stamps
 * alone are `0,1,2,…` for ANY same-length result. A query that returns the same
 * row count but reordered (or entirely different) rows would keep an identical
 * stamp-only fingerprint, and Enter/Space would reuse a cached index against
 * the new rows and drill the wrong segment. Pairing each stamp with the row's
 * identity label ties the fingerprint to what each position actually
 * represents, so the cache is dropped whenever the rows behind the chart move.
 *
 * Cartesian rows (Bar/Line/Area) label their category with `x`; pie slices
 * carry it as `name` instead (see `PieChartCase`'s `pieData`, which has no `x`).
 * A pie fingerprint that read only `x` would collapse every slice to the empty
 * identity, so a reordered same-length pie query would still collide. Fall back
 * to `name` when `x` is absent so pie reorders invalidate the cache too.
 */
export function chartDataFingerprint(
  chartData: Record<string, unknown>[]
): string {
  return JSON.stringify(
    chartData.map(datum => {
      const stamp = sourceRowIndexOf(datum) ?? 'x';
      const { x, name } = datum;
      let identity = '';
      if (typeof x === 'string') {
        identity = x;
      } else if (typeof name === 'string') {
        identity = name;
      }
      return [stamp, identity];
    })
  );
}

/**
 * Returns true when there is an active selection that does NOT match this
 * row+series — i.e. this element should be dimmed.
 */
export function isDimmed(
  active: { rowIndex: number; seriesKey: string } | null | undefined,
  rowIndex: number,
  seriesKey: string
): boolean {
  if (!active) {
    return false;
  }
  return active.rowIndex !== rowIndex || active.seriesKey !== seriesKey;
}

/**
 * Opacity for a chart mark: full (1) when not dimmed, reduced when dimmed.
 *
 * 0.55, not the 0.35 this used to be, because the dimmed value is relative to
 * the fill and the palette moved: chart fills are now pastel (L* mean 66 on
 * white, up from 44), so the same 0.35 that left a dimmed bar at 1.44:1 against
 * the card would drop it to 1.24:1 — faint enough to read as an empty slot
 * rather than an un-selected one. 0.55 restores the dimmed bar to 1.41:1,
 * matching what the old palette rendered.
 *
 * Tie this to the palette, not to taste: if the fills are ever re-darkened,
 * this number moves back down with them.
 */
export function markOpacity(dimmed: boolean): number {
  return dimmed ? 0.55 : 1;
}

/**
 * Returns a keyboard handler (Enter / Space → dispatch) for a single
 * interactive chart cell (Bar, Pie, Combo Bar). Unlike
 * `useSeriesKeyboardSelection`, which operates at container level, this
 * targets individual focusable marks whose datum and seriesKey are known at
 * factory time.
 *
 * Returns `undefined` when `onDatapointSelect` is absent or when `datum`
 * carries no finite `__sourceRowIndex` — lets callers assign the result
 * directly to `onKeyDown` without an extra null-check.
 */
export function makeCellKeyDownHandler(
  datum: unknown,
  seriesKey: string,
  onDatapointSelect: ((sel: ChartDatapointSelection) => void) | undefined
): ((e: KeyboardEvent) => void) | undefined {
  if (!onDatapointSelect) {
    return undefined;
  }
  const rowIndex = sourceRowIndexOf(datum);
  if (rowIndex === null) {
    return undefined;
  }
  return (e: KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') {
      return;
    }
    e.preventDefault();
    // This mark knows exactly which row it is, so the container-level
    // fallback must not also run: in a combo chart the event bubbled to
    // `handleContainerKeyDown`, which — with no prior hover — dispatched row
    // 0 immediately after, and the wrong row won.
    e.stopPropagation();
    onDatapointSelect({ rowIndex, seriesKey });
  };
}

/**
 * Returns a Recharts onClick handler for any series element — Line, Area, Bar
 * or Pie. `resolveSourceRowIndex` absorbs the shape differences between them.
 * Call inside useCallback.
 */
export function makeSeriesClickHandler(
  seriesKey: string,
  onDatapointSelect: ((sel: ChartDatapointSelection) => void) | undefined
): ((data: unknown) => void) | undefined {
  if (!onDatapointSelect) {
    return undefined;
  }
  return (data: unknown) => {
    const rowIndex = resolveSourceRowIndex(data);
    if (rowIndex === null) {
      return;
    }
    onDatapointSelect({ rowIndex, seriesKey });
  };
}

/**
 * Hover-tracking + keyboard-activation logic shared by Line and Area chart
 * cases. Recharts fires `onMouseMove` with a CategoricalChartState that
 * includes `activePayload: [{ payload: datum, dataKey }]`; the last hovered
 * point is stashed in a ref so Enter/Space can dispatch without a re-render.
 *
 * A keyboard-only user who tabs into the chart and presses Enter/Space
 * without ever hovering defaults to the first row of the first series,
 * rather than no-op'ing. Does nothing (no throw) when the chart has no data
 * or no series.
 */
export function useSeriesKeyboardSelection({
  chartData,
  seriesKeys,
  onDatapointSelect,
}: {
  chartData: Record<string, unknown>[];
  seriesKeys: string[];
  onDatapointSelect: ((sel: ChartDatapointSelection) => void) | undefined;
}): {
  handleMouseMove: (state: unknown) => void;
  handleContainerKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
} {
  // The cached hover is tagged with a CONTENT fingerprint of the chart data it
  // was captured against — not the array reference. Callers rebuild `chartData`
  // inline every render, so a reference check invalidated the cache on any
  // benign re-render (a click elsewhere, a resize, a live update) and Enter/Space
  // then drilled the first row instead of the hovered one. A keypress after the
  // chart's inputs genuinely change still must NOT drill a stale row, so the
  // keydown handler honours the cache only when the fingerprint still matches the
  // data on screen and the remembered series still exists.
  const lastHoveredRef = useRef<{
    selection: ChartDatapointSelection;
    fingerprint: string;
  } | null>(null);

  const handleMouseMove = useCallback(
    (state: unknown) => {
      if (!onDatapointSelect) {
        return;
      }
      const activePayload = (state as Record<string, unknown> | null)
        ?.activePayload as
        | Array<{ payload: unknown; dataKey?: string }>
        | undefined;
      if (!activePayload?.length) {
        return;
      }
      const entry = activePayload[0];
      const rowIndex = resolveSourceRowIndex(entry.payload);
      if (rowIndex === null) {
        // A non-drillable mark (e.g. the Top-N "Other" slice, which carries no
        // __sourceRowIndex) must clear the remembered hover — otherwise
        // Enter/Space would drill the previously hovered real mark instead.
        lastHoveredRef.current = null;
        return;
      }
      const key =
        typeof entry.dataKey === 'string' ? entry.dataKey : seriesKeys[0];
      lastHoveredRef.current = {
        selection: { rowIndex, seriesKey: key },
        fingerprint: chartDataFingerprint(chartData),
      };
    },
    [onDatapointSelect, seriesKeys, chartData]
  );

  const handleContainerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!onDatapointSelect || (e.key !== 'Enter' && e.key !== ' ')) {
        return;
      }
      e.preventDefault();
      const cached = lastHoveredRef.current;
      // Honour the cached hover only when its content fingerprint still matches
      // the data currently shown and its series still exists; a change to either
      // since the hover invalidates it and we fall through to the first-row
      // default.
      if (
        cached &&
        cached.fingerprint === chartDataFingerprint(chartData) &&
        seriesKeys.includes(cached.selection.seriesKey)
      ) {
        onDatapointSelect(cached.selection);
        return;
      }
      if (chartData.length === 0 || seriesKeys.length === 0) {
        return;
      }
      const rowIndex = sourceRowIndexOf(chartData[0]);
      if (rowIndex === null) {
        return;
      }
      onDatapointSelect({ rowIndex, seriesKey: seriesKeys[0] });
    },
    [onDatapointSelect, chartData, seriesKeys]
  );

  return { handleMouseMove, handleContainerKeyDown };
}
